import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { useTranslation } from '../LanguageContext';
import { SERIES_COLORS } from './DonutChart';

const usd = (value) => `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
})}`;
const date = (value) => value ? new Date(value).toLocaleDateString('en-GB') : '—';
const dateTime = (value) => value ? new Date(value).toLocaleString('en-GB') : '—';

const PAYOUT_STYLES = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    approved: 'bg-sky-50 text-sky-700 border-sky-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    rejected: 'bg-red-50 text-red-600 border-red-200',
};

function Stat({ label, value, tone = 'slate', hint }) {
    const tones = {
        slate: 'text-slate-900',
        emerald: 'text-emerald-600',
        amber: 'text-amber-600',
        red: 'text-red-600',
    };
    return (
        <div className="rounded-xl bg-white border border-slate-200 p-3">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{label}</p>
            <p className={`text-lg font-black tabular-nums ${tones[tone]}`}>{value}</p>
            {hint && <p className="text-[10px] text-slate-400 mt-0.5">{hint}</p>}
        </div>
    );
}

/** "I sent the money by hand" — close the balance and leave an audit record. */
function SettlePanel({ detail, onDone, t }) {
    const settleable = detail.balance.settleable_usd;
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [method, setMethod] = useState('bank');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [confirming, setConfirming] = useState(false);

    const openClaim = (detail.payouts || []).find(p => ['pending', 'approved'].includes(p.status));
    const target = amount === '' ? settleable : Number(amount);

    const submit = async () => {
        setBusy(true);
        setError('');
        try {
            const res = await api.post(`/api/admin/finance/${detail.user.id}/`, {
                action: 'settle',
                amount_usd: amount === '' ? null : amount,
                admin_note: note,
                method,
                account_details: openClaim?.account_details || '',
            });
            if (res.status === 200) {
                setAmount(''); setNote(''); setConfirming(false);
                onDone(res.data);
            }
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'nothing_to_settle' ? t('adminFinance.nothingToSettle')
                : code === 'invalid_amount' ? t('adminFinance.invalidAmount')
                    : t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    if (settleable <= 0) {
        return (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-center">
                <p className="text-sm font-semibold text-slate-500">{t('adminFinance.balanceClear')}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{t('adminFinance.balanceClearHint')}</p>
            </div>
        );
    }

    return (
        <div className="rounded-xl border-2 border-primary-200 bg-primary-50/40 p-4">
            <h4 className="text-sm font-black text-slate-800">{t('adminFinance.manualPayTitle')}</h4>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-3 leading-relaxed">
                {t('adminFinance.manualPayHint')}
            </p>

            {openClaim && (
                <div className="rounded-lg bg-white border border-amber-200 p-2.5 mb-3">
                    <p className="text-[11px] font-bold text-amber-700">{t('adminFinance.openRequest')}</p>
                    <p className="text-sm font-black text-slate-800">{usd(openClaim.amount_usd)}</p>
                    <p className="text-[11px] text-slate-500">
                        {openClaim.method} · {date(openClaim.created_at)}
                    </p>
                    {openClaim.account_details && (
                        <p className="text-[11px] font-mono text-slate-600 break-all mt-0.5">
                            {openClaim.account_details}
                        </p>
                    )}
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                    <label className="label text-[11px]">{t('adminFinance.amountLabel')}</label>
                    <input type="number" min="0" step="0.01" className="input py-2 text-sm bg-white"
                        value={amount} placeholder={usd(settleable)}
                        onChange={(e) => { setAmount(e.target.value); setConfirming(false); }} />
                    <p className="text-[10px] text-slate-400 mt-1">
                        {t('adminFinance.blankMeansAll')} — {usd(settleable)}
                    </p>
                </div>
                <div>
                    <label className="label text-[11px]">{t('adminFinance.methodLabel')}</label>
                    <select className="input py-2 text-sm bg-white" value={method}
                        onChange={(e) => setMethod(e.target.value)}>
                        <option value="bank">{t('finance.methodBank')}</option>
                        <option value="wise">Wise</option>
                        <option value="paypal">PayPal</option>
                    </select>
                </div>
            </div>

            <label className="label text-[11px] mt-2">{t('adminFinance.noteLabel')}</label>
            <input className="input py-2 text-sm bg-white" value={note}
                placeholder={t('adminFinance.notePlaceholder')}
                onChange={(e) => setNote(e.target.value)} />

            {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

            {confirming ? (
                <div className="mt-3 rounded-lg bg-white border border-primary-300 p-3">
                    <p className="text-xs text-slate-700 leading-relaxed">
                        {t('adminFinance.confirmLine1')} <strong>{usd(target)}</strong> {t('adminFinance.confirmLine2')}
                    </p>
                    <div className="flex gap-2 mt-2.5">
                        <button onClick={() => setConfirming(false)} className="btn-secondary flex-1 py-2 text-xs">
                            {t('campaigns.cancel')}
                        </button>
                        <button onClick={submit} disabled={busy}
                            className="btn-success flex-1 py-2 text-xs flex items-center justify-center gap-2">
                            {busy && <span className="spinner-sm" />}
                            {t('adminFinance.confirmSettle')}
                        </button>
                    </div>
                </div>
            ) : (
                <button onClick={() => setConfirming(true)}
                    className="btn-success w-full py-2.5 text-sm mt-3 font-bold">
                    {t('adminFinance.settleBtn')}
                </button>
            )}
        </div>
    );
}

/** Hand-correct a wallet — a refunded charge, a goodwill credit, an off-Stripe top-up. */
function WalletAdjustPanel({ userId, onDone, t }) {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [open, setOpen] = useState(false);

    const value = Number(amount);
    const valid = amount !== '' && !Number.isNaN(value) && value !== 0;

    const submit = async () => {
        if (!valid) return;
        setBusy(true);
        setError('');
        try {
            await api.post(`/api/admin/finance/${userId}/`, {
                action: 'wallet_adjust', amount_usd: amount, admin_note: note,
            });
            setAmount(''); setNote(''); setOpen(false);
            onDone();
        } catch (err) {
            const code = err.response?.data?.error;
            setError(code === 'insufficient_balance' ? t('adminFinance.walletTooLow')
                : code === 'invalid_amount' ? t('adminFinance.invalidAmount')
                    : t('campaigns.saveError'));
        } finally { setBusy(false); }
    };

    if (!open) {
        return (
            <button onClick={() => setOpen(true)}
                className="mt-3 w-full py-2 rounded-lg text-[11px] font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition">
                {t('adminFinance.walletAdjust')}
            </button>
        );
    }

    return (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-[11px] font-bold text-slate-600">{t('adminFinance.walletAdjust')}</p>
            <p className="text-[10px] text-slate-400 mb-2 leading-relaxed">{t('adminFinance.walletAdjustHint')}</p>
            <div className="flex gap-2">
                <input type="number" step="0.01" className="input py-2 text-sm bg-white w-32"
                    value={amount} placeholder="-50.00"
                    onChange={(e) => setAmount(e.target.value)} />
                <input className="input py-2 text-sm bg-white flex-1" value={note}
                    placeholder={t('adminFinance.notePlaceholder')}
                    onChange={(e) => setNote(e.target.value)} />
            </div>
            {error && <p className="text-[11px] text-red-600 mt-1.5">{error}</p>}
            <div className="flex gap-2 mt-2">
                <button onClick={() => { setOpen(false); setError(''); }}
                    className="btn-secondary flex-1 py-1.5 text-xs">{t('campaigns.cancel')}</button>
                <button onClick={submit} disabled={!valid || busy}
                    className="btn-primary flex-1 py-1.5 text-xs flex items-center justify-center gap-2">
                    {busy && <span className="spinner-sm" />}
                    {t('adminFinance.walletApply')}
                </button>
            </div>
        </div>
    );
}

function UserDetail({ userId, onClose, onChanged }) {
    const { t } = useTranslation();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/api/admin/finance/${userId}/`);
            setDetail(res.data);
        } finally { setLoading(false); }
    }, [userId]);

    useEffect(() => { load(); }, [load]);

    const decide = async (payoutId, decision) => {
        setBusy(true);
        try {
            await api.post(`/api/admin/finance/${userId}/`, {
                action: 'payout_decide', payout_id: payoutId, decision,
            });
            await load();
            onChanged();
        } finally { setBusy(false); }
    };

    const resetStars = async () => {
        if (!window.confirm(t('adminFinance.resetStarsConfirm'))) return;
        setBusy(true);
        try {
            await api.post(`/api/admin/finance/${userId}/`, { action: 'reset_stars' });
            await load();
            onChanged();
        } finally { setBusy(false); }
    };

    const tabs = [
        { key: 'overview', label: t('adminFinance.tabOverview') },
        { key: 'ledger', label: t('adminFinance.tabLedger') },
        { key: 'payouts', label: t('adminFinance.tabPayouts') },
        { key: 'wallet', label: t('adminFinance.tabWallet') },
        { key: 'stars', label: t('adminFinance.tabStars') },
    ];

    return (
        <div className="fixed inset-0 z-[9000] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-slate-50 w-full sm:max-w-3xl h-[94vh] sm:h-[90vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden">

                {loading || !detail ? (
                    <div className="flex-1 flex items-center justify-center"><div className="spinner" /></div>
                ) : (
                    <>
                        {/* Header */}
                        <div className="bg-white border-b border-slate-100 px-4 sm:px-5 py-4 flex items-center gap-3 flex-shrink-0">
                            <img src={detail.user.profile_picture || '/logo192.png'} alt=""
                                className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-100" />
                            <div className="flex-1 min-w-0">
                                <p className="text-base font-black text-slate-900 truncate">{detail.user.username}</p>
                                <p className="text-[11px] text-slate-400 truncate">
                                    {detail.user.email || '—'} · {detail.user.account_type} · {t('adminFinance.joined')} {date(detail.user.date_joined)}
                                </p>
                            </div>
                            <button onClick={onClose}
                                className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 transition flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="bg-white px-4 sm:px-5 pb-2 flex gap-1 flex-shrink-0 overflow-x-auto scrollbar-hide">
                            {tabs.map(item => (
                                <button key={item.key} onClick={() => setTab(item.key)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition ${tab === item.key
                                        ? 'bg-primary-600 text-white'
                                        : 'text-slate-500 hover:bg-slate-100'}`}>
                                    {item.label}
                                </button>
                            ))}
                        </div>

                        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 sm:px-5 py-4 space-y-4">

                            {tab === 'overview' && (
                                <>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                        <Stat label={t('adminFinance.available')} value={usd(detail.balance.available_usd)}
                                            tone={detail.balance.available_usd > 0 ? 'emerald' : 'slate'} />
                                        <Stat label={t('adminFinance.settleable')} value={usd(detail.balance.settleable_usd)}
                                            hint={t('adminFinance.settleableHint')} />
                                        <Stat label={t('finance.pending')} value={usd(detail.balance.pending_usd)} tone="amber" />
                                        <Stat label={t('finance.gross')} value={usd(detail.balance.lifetime_gross_usd)} />
                                        <Stat label={t('adminFinance.feeTaken')} value={usd(detail.balance.lifetime_fee_usd)} />
                                        <Stat label={t('finance.withdrawn')} value={usd(detail.balance.withdrawn_usd)} />
                                    </div>

                                    <SettlePanel detail={detail} t={t}
                                        onDone={() => { load(); onChanged(); }} />

                                    {/* Source breakdown */}
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <h4 className="text-sm font-bold text-slate-800 mb-2">{t('finance.breakdownTitle')}</h4>
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-slate-400 border-b border-slate-100">
                                                    <th className="text-left font-semibold py-1.5">{t('finance.source')}</th>
                                                    <th className="text-right font-semibold py-1.5">{t('finance.gross')}</th>
                                                    <th className="text-right font-semibold py-1.5">{t('finance.fee')}</th>
                                                    <th className="text-right font-semibold py-1.5">{t('finance.net')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.breakdown.map(row => (
                                                    <tr key={row.source} className="border-b border-slate-50">
                                                        <td className="py-1.5 text-slate-700 font-medium">
                                                            <span className="inline-block w-2 h-2 rounded-sm mr-1.5"
                                                                style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                                                            {t(`finance.source_${row.source}`)}
                                                            <span className="text-slate-400 ml-1">({row.count})</span>
                                                        </td>
                                                        <td className="py-1.5 text-right text-slate-600 tabular-nums">{usd(row.gross_usd)}</td>
                                                        <td className="py-1.5 text-right text-slate-400 tabular-nums">−{usd(row.fee_usd)}</td>
                                                        <td className="py-1.5 text-right text-slate-900 font-bold tabular-nums">{usd(row.net_usd)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Campaign wallet & escrow */}
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <h4 className="text-sm font-bold text-slate-800 mb-3">{t('wallet.cardTitle')}</h4>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            <Stat label={t('wallet.spendable')} value={usd(detail.wallet?.balance_usd)}
                                                tone={detail.wallet?.balance_usd > 0 ? 'emerald' : 'slate'} />
                                            <Stat label={t('wallet.deposited')} value={usd(detail.wallet?.deposited_usd)} />
                                            <Stat label={t('finance.escrowIncoming')} value={usd(detail.wallet?.escrow_incoming_usd)} tone="amber" />
                                            <Stat label={t('finance.escrowOutgoing')} value={usd(detail.wallet?.escrow_outgoing_usd)} />
                                        </div>
                                        <WalletAdjustPanel userId={detail.user.id}
                                            onDone={() => { load(); onChanged(); }} t={t} />
                                    </div>

                                    {/* Stars — chat gifting only */}
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <div className="flex items-center justify-between mb-3">
                                            <div>
                                                <h4 className="text-sm font-bold text-slate-800">{t('finance.starWallet')}</h4>
                                                <p className="text-[10px] text-slate-400">{t('finance.starPurposeNote')}</p>
                                            </div>
                                            <button onClick={resetStars} disabled={busy}
                                                className="px-3 py-1.5 rounded-lg text-[11px] font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition">
                                                {t('adminFinance.resetStars')}
                                            </button>
                                        </div>
                                        <Stat label={t('finance.starBalance')} value={detail.user.stars} />
                                    </div>

                                    {/* Deals */}
                                    {detail.deals.length > 0 && (
                                        <div className="rounded-xl border border-slate-200 bg-white p-4">
                                            <h4 className="text-sm font-bold text-slate-800 mb-2">{t('adminFinance.deals')}</h4>
                                            <div className="space-y-1.5">
                                                {detail.deals.map(deal => (
                                                    <div key={deal.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-50 last:border-0">
                                                        <span className="flex-1 min-w-0 truncate text-slate-700">{deal.campaign_title}</span>
                                                        <span className="text-slate-400 whitespace-nowrap">
                                                            {deal.brand?.username} → {deal.creator?.username}
                                                        </span>
                                                        <span className="font-bold text-slate-800 tabular-nums whitespace-nowrap">{usd(deal.amount_usd)}</span>
                                                        <span className="text-[10px] font-semibold text-slate-500 whitespace-nowrap">
                                                            {t(`campaigns.escrow_${deal.escrow_status}`)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}

                            {tab === 'ledger' && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4">
                                    <h4 className="text-sm font-bold text-slate-800 mb-2">
                                        {t('adminFinance.tabLedger')} ({detail.earnings.length})
                                    </h4>
                                    {detail.earnings.length === 0 ? (
                                        <p className="text-xs text-slate-400 py-4 text-center">{t('adminFinance.noRows')}</p>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="text-slate-400 border-b border-slate-100">
                                                    <th className="text-left font-semibold py-1.5">{t('finance.source')}</th>
                                                    <th className="text-left font-semibold py-1.5">{t('adminFinance.description')}</th>
                                                    <th className="text-right font-semibold py-1.5">{t('finance.net')}</th>
                                                    <th className="text-right font-semibold py-1.5">{t('adminFinance.state')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {detail.earnings.map(row => (
                                                    <tr key={row.id} className="border-b border-slate-50">
                                                        <td className="py-1.5 text-slate-700">
                                                            <span className="inline-block w-2 h-2 rounded-sm mr-1.5"
                                                                style={{ backgroundColor: SERIES_COLORS[row.source] }} />
                                                            {t(`finance.source_${row.source}`)}
                                                        </td>
                                                        <td className="py-1.5 text-slate-500 truncate max-w-[140px]">
                                                            {row.description || row.reference}
                                                            <span className="block text-[10px] text-slate-400">{date(row.created_at)}</span>
                                                        </td>
                                                        <td className="py-1.5 text-right font-bold text-slate-800 tabular-nums">{usd(row.net_usd)}</td>
                                                        <td className="py-1.5 text-right">
                                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${row.status === 'paid'
                                                                ? 'bg-slate-100 text-slate-500'
                                                                : row.status === 'pending'
                                                                    ? 'bg-amber-50 text-amber-700'
                                                                    : 'bg-emerald-50 text-emerald-700'}`}>
                                                                {t(`adminFinance.earningStatus_${row.status}`)}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}

                            {tab === 'payouts' && (
                                <div className="space-y-2">
                                    {detail.payouts.length === 0 ? (
                                        <p className="text-xs text-slate-400 py-6 text-center">{t('admin.noPayouts')}</p>
                                    ) : detail.payouts.map(payout => (
                                        <div key={payout.id} className="rounded-xl border border-slate-200 bg-white p-3">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-base font-black text-slate-900">{usd(payout.amount_usd)}</p>
                                                    <p className="text-[11px] text-slate-400">
                                                        {t('adminFinance.requested')} {dateTime(payout.created_at)}
                                                        {payout.processed_at && ` · ${t('adminFinance.processed')} ${dateTime(payout.processed_at)}`}
                                                    </p>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                                        {payout.method}
                                                        {payout.account_details && (
                                                            <span className="font-mono text-slate-600 break-all"> · {payout.account_details}</span>
                                                        )}
                                                    </p>
                                                    {payout.admin_note && (
                                                        <p className="text-[11px] text-slate-500 italic mt-0.5">"{payout.admin_note}"</p>
                                                    )}
                                                </div>
                                                <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold border flex-shrink-0 ${PAYOUT_STYLES[payout.status]}`}>
                                                    {t(`finance.payoutStatus_${payout.status}`)}
                                                </span>
                                            </div>
                                            {['pending', 'approved'].includes(payout.status) && (
                                                <div className="flex gap-2 mt-3">
                                                    {payout.status === 'pending' && (
                                                        <button onClick={() => decide(payout.id, 'approve')} disabled={busy}
                                                            className="btn-secondary flex-1 py-1.5 text-xs">{t('admin.approve')}</button>
                                                    )}
                                                    <button onClick={() => decide(payout.id, 'paid')} disabled={busy}
                                                        className="btn-success flex-1 py-1.5 text-xs">{t('admin.markPaid')}</button>
                                                    <button onClick={() => decide(payout.id, 'reject')} disabled={busy}
                                                        className="px-3 py-1.5 rounded-xl text-xs font-semibold text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 transition">
                                                        {t('admin.reject')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {tab === 'wallet' && (
                                <>
                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <h4 className="text-sm font-bold text-slate-800 mb-2">
                                            {t('wallet.history')} ({(detail.wallet_transactions || []).length})
                                        </h4>
                                        {(detail.wallet_transactions || []).length === 0 ? (
                                            <p className="text-xs text-slate-400 py-4 text-center">{t('adminFinance.noRows')}</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {detail.wallet_transactions.map(row => (
                                                    <div key={row.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-50 last:border-0">
                                                        <span className="text-slate-500 whitespace-nowrap">{date(row.created_at)}</span>
                                                        <span className="flex-1 min-w-0 truncate text-slate-700">
                                                            {t(`wallet.kind_${row.kind}`)}
                                                            {row.counterparty && <span className="text-slate-400"> · {row.counterparty}</span>}
                                                            {row.note && <span className="text-slate-400"> · {row.note}</span>}
                                                        </span>
                                                        <span className={`font-bold tabular-nums whitespace-nowrap ${row.amount_usd < 0 ? 'text-slate-500' : 'text-emerald-600'}`}>
                                                            {row.amount_usd < 0 ? '−' : '+'}{usd(Math.abs(row.amount_usd))}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 tabular-nums whitespace-nowrap w-20 text-right">
                                                            {usd(row.balance_after)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="rounded-xl border border-slate-200 bg-white p-4">
                                        <h4 className="text-sm font-bold text-slate-800 mb-2">
                                            {t('adminFinance.deposits')} ({(detail.deposits || []).length})
                                        </h4>
                                        {(detail.deposits || []).length === 0 ? (
                                            <p className="text-xs text-slate-400 py-4 text-center">{t('adminFinance.noRows')}</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {detail.deposits.map(row => (
                                                    <div key={row.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-50 last:border-0">
                                                        <span className="text-slate-500 whitespace-nowrap">{date(row.created_at)}</span>
                                                        <span className="flex-1 min-w-0 truncate font-mono text-[10px] text-slate-400">
                                                            {row.payment_intent_id}
                                                        </span>
                                                        <span className="font-bold text-slate-800 tabular-nums whitespace-nowrap">
                                                            {usd(row.amount_usd)}
                                                        </span>
                                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${row.status === 'succeeded'
                                                            ? 'bg-emerald-50 text-emerald-700'
                                                            : row.status === 'pending'
                                                                ? 'bg-amber-50 text-amber-700'
                                                                : 'bg-red-50 text-red-600'}`}>
                                                            {t(`wallet.depositStatus_${row.status}`)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}

                            {tab === 'stars' && (
                                <div className="rounded-xl border border-slate-200 bg-white p-4">
                                    <h4 className="text-sm font-bold text-slate-800 mb-2">
                                        {t('adminFinance.tabStars')} ({detail.star_transactions.length})
                                    </h4>
                                    {detail.star_transactions.length === 0 ? (
                                        <p className="text-xs text-slate-400 py-4 text-center">{t('adminFinance.noRows')}</p>
                                    ) : (
                                        <div className="space-y-1">
                                            {detail.star_transactions.map(row => (
                                                <div key={row.id} className="flex items-center gap-2 text-xs py-1.5 border-b border-slate-50 last:border-0">
                                                    <span className="text-slate-500 whitespace-nowrap">{date(row.created_at)}</span>
                                                    <span className="flex-1 min-w-0 truncate text-slate-700">
                                                        {t(`adminFinance.starKind_${row.kind}`)}
                                                        <span className="text-slate-400"> · {row.sender || '—'} → {row.receiver || '—'}</span>
                                                    </span>
                                                    <span className="font-bold text-slate-800 tabular-nums whitespace-nowrap">{row.amount} ★</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/** The finance desk: every account with money moving, and what to do about it. */
function AdminFinance() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');   // all | requests | owed
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/api/admin/finance/', {
                params: query ? { q: query } : {},
            });
            setData(res.data);
        } finally { setLoading(false); }
    }, [query]);

    useEffect(() => {
        const timer = setTimeout(load, query ? 300 : 0);
        return () => clearTimeout(timer);
    }, [load, query]);

    if (loading) return <div className="flex justify-center py-10"><div className="spinner" /></div>;
    if (!data) return <p className="text-sm text-slate-400 text-center py-10">{t('admin.unauthorizedMsg')}</p>;

    const rows = (data.users || []).filter(row => {
        if (filter === 'requests') return row.open_requests > 0;
        if (filter === 'owed') return row.available_usd > 0;
        return true;
    });

    const filters = [
        { key: 'all', label: t('adminFinance.filterAll') },
        { key: 'requests', label: `${t('adminFinance.filterRequests')} (${data.totals.open_requests})` },
        { key: 'owed', label: t('adminFinance.filterOwed') },
    ];

    return (
        <div className="space-y-4">
            {/* Totals */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <Stat label={t('adminFinance.totalOwed')} value={usd(data.totals.owed_usd)} tone="emerald"
                    hint={t('adminFinance.totalOwedHint')} />
                <Stat label={t('adminFinance.openRequests')} value={data.totals.open_requests}
                    tone={data.totals.open_requests ? 'amber' : 'slate'} />
                <Stat label={t('adminFinance.totalPaid')} value={usd(data.totals.paid_usd)} />
                <Stat label={t('adminFinance.totalWallet')} value={usd(data.totals.wallet_usd)}
                    hint={t('adminFinance.totalWalletHint')} />
                <Stat label={t('adminFinance.accounts')} value={data.totals.accounts} />
            </div>

            {/* Search + filters */}
            <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2">
                <input className="input py-2 text-sm" value={query}
                    placeholder={t('adminFinance.searchPlaceholder')}
                    onChange={(e) => setQuery(e.target.value)} />
                <div className="flex gap-2 overflow-x-auto scrollbar-hide">
                    {filters.map(item => (
                        <button key={item.key} onClick={() => setFilter(item.key)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap border transition ${filter === item.key
                                ? 'bg-primary-600 text-white border-primary-600'
                                : 'bg-white text-slate-500 border-slate-200 hover:border-primary-300'}`}>
                            {item.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* User rows */}
            {rows.length === 0 ? (
                <p className="text-sm text-slate-400 text-center py-10">{t('adminFinance.noAccounts')}</p>
            ) : (
                <div className="space-y-2">
                    {rows.map(row => (
                        <button key={row.user_id} onClick={() => setSelected(row.user_id)}
                            className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:border-primary-300 hover:shadow-sm transition flex items-center gap-3">
                            <img src={row.profile_picture || '/logo192.png'} alt=""
                                className="w-9 h-9 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <p className="text-sm font-bold text-slate-800 truncate">{row.username}</p>
                                    {row.open_requests > 0 && (
                                        <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-black whitespace-nowrap">
                                            {row.open_requests} {t('adminFinance.requestBadge')}
                                        </span>
                                    )}
                                </div>
                                <p className="text-[11px] text-slate-400 truncate">
                                    {row.account_type}
                                    {row.wallet_usd > 0 && ` · ${usd(row.wallet_usd)} ${t('adminFinance.inWallet')}`}
                                    {row.escrow_incoming_usd > 0 && ` · ${usd(row.escrow_incoming_usd)} ${t('adminFinance.inEscrow')}`}
                                    {row.stars > 0 && ` · ${row.stars} ★`}
                                </p>
                            </div>
                            <div className="text-right flex-shrink-0">
                                {/* An open request holds the balance, so show what is
                                    actually payable rather than the zeroed figure. */}
                                <p className={`text-sm font-black tabular-nums ${row.open_requests > 0
                                    ? 'text-amber-600'
                                    : row.settleable_usd > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                                    {usd(row.settleable_usd)}
                                </p>
                                <p className="text-[10px] text-slate-400 tabular-nums">
                                    {row.open_requests > 0
                                        ? `${t('adminFinance.requestedAmount')} ${usd(row.requested_usd)}`
                                        : `${t('finance.lifetimeNet')} ${usd(row.lifetime_net_usd)}`}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {selected && (
                <UserDetail userId={selected}
                    onClose={() => setSelected(null)}
                    onChanged={load} />
            )}
        </div>
    );
}

export default AdminFinance;
